/**
 * usePortalPWA — everything the Public Song Portal needs to feel like a
 * real installable app that works on the drive to church.
 *
 * Responsibilities:
 *   • Capture the `beforeinstallprompt` event and expose an `install()` fn
 *   • Track `isInstalled` (display-mode: standalone or iOS `navigator.standalone`)
 *   • Detect iOS Safari (which has NO install prompt — must show manual steps)
 *   • Track `isOffline` so the portal can render a banner
 *   • `saveForOffline(serviceId)` → posts `PORTAL_PRECACHE` to the service
 *     worker with the API URL + any lead-sheet PDFs
 *   • Persist the last-viewed service id in localStorage so a cold launch
 *     of the installed app boots back into the last set (used by /portal
 *     landing route)
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const LAST_KEY = 'portal.lastServiceId';
const LAST_URL_KEY = 'portal.lastUrl';
const SAVED_KEY = 'portal.savedServices'; // JSON array of ids

const API = process.env.REACT_APP_BACKEND_URL || '';

function readSaved() {
  try { return JSON.parse(localStorage.getItem(SAVED_KEY) || '[]'); }
  catch { return []; }
}
function writeSaved(list) {
  try { localStorage.setItem(SAVED_KEY, JSON.stringify(list)); }
  catch { /* quota */ }
}

export function usePortalPWA({ serviceId } = {}) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isOffline, setIsOffline] = useState(
    typeof navigator !== 'undefined' && navigator.onLine === false,
  );
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [savedIds, setSavedIds] = useState(readSaved);
  const swMsgListenerRef = useRef(null);

  // Detect installed / standalone mode
  useEffect(() => {
    const check = () => {
      const standalone = window.matchMedia?.('(display-mode: standalone)')?.matches
        || window.navigator.standalone === true;
      setIsInstalled(Boolean(standalone));
    };
    check();
    const mq = window.matchMedia?.('(display-mode: standalone)');
    mq?.addEventListener?.('change', check);
    return () => mq?.removeEventListener?.('change', check);
  }, []);

  // beforeinstallprompt capture
  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // Online/offline
  useEffect(() => {
    const on = () => setIsOffline(false);
    const off = () => setIsOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  // Persist last viewed service so the installed app boots back into it
  useEffect(() => {
    if (!serviceId) return;
    try {
      localStorage.setItem(LAST_KEY, serviceId);
      localStorage.setItem(LAST_URL_KEY, window.location.pathname);
    } catch { /* quota */ }
  }, [serviceId]);

  // iOS detection (Safari on iPhone/iPad has no beforeinstallprompt)
  const isIOS = typeof navigator !== 'undefined'
    && /iP(ad|hone|od)/.test(navigator.userAgent)
    && !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);

  const canInstall = Boolean(deferredPrompt) || (isIOS && !isInstalled);

  const install = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      return choice?.outcome === 'accepted';
    }
    return false;
  }, [deferredPrompt]);

  const saveForOffline = useCallback(async (id, extraUrls = []) => {
    const sid = id || serviceId;
    if (!sid) return false;
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
      setSaveState('error');
      return false;
    }
    setSaveState('saving');
    const urls = [
      `${API}/api/public/worship/services/${sid}`,
      ...extraUrls,
    ];
    return new Promise((resolve) => {
      // one-shot listener
      const onMsg = (ev) => {
        if (ev.data?.type === 'PORTAL_PRECACHED') {
          navigator.serviceWorker.removeEventListener('message', onMsg);
          const ok = ev.data.results?.every((r) => r.ok);
          setSaveState(ok ? 'saved' : 'error');
          if (ok) {
            const next = Array.from(new Set([...readSaved(), sid]));
            writeSaved(next);
            setSavedIds(next);
          }
          resolve(ok);
        }
      };
      navigator.serviceWorker.addEventListener('message', onMsg);
      swMsgListenerRef.current = onMsg;
      navigator.serviceWorker.controller.postMessage({
        type: 'PORTAL_PRECACHE',
        urls,
      });
      // safety timeout
      setTimeout(() => {
        navigator.serviceWorker.removeEventListener('message', onMsg);
        if (saveState === 'saving') {
          setSaveState('error');
          resolve(false);
        }
      }, 15000);
    });
  }, [serviceId]);

  const clearOffline = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) return false;
    return new Promise((resolve) => {
      const onMsg = (ev) => {
        if (ev.data?.type === 'PORTAL_CLEARED') {
          navigator.serviceWorker.removeEventListener('message', onMsg);
          writeSaved([]);
          setSavedIds([]);
          setSaveState('idle');
          resolve(true);
        }
      };
      navigator.serviceWorker.addEventListener('message', onMsg);
      navigator.serviceWorker.controller.postMessage({ type: 'PORTAL_CLEAR' });
    });
  }, []);

  const isSavedOffline = Boolean(serviceId && savedIds.includes(serviceId));

  return {
    isInstalled,
    canInstall,
    isIOS,
    isOffline,
    install,
    saveForOffline,
    clearOffline,
    saveState,
    isSavedOffline,
    savedCount: savedIds.length,
  };
}

/** For the /portal landing route: read last service id and last url. */
export function readLastPortal() {
  try {
    return {
      serviceId: localStorage.getItem(LAST_KEY),
      url: localStorage.getItem(LAST_URL_KEY),
    };
  } catch {
    return { serviceId: null, url: null };
  }
}
